import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageCircle, X, BellRing, Check } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { enablePush, pushEnabled, pushSupported, isIOS } from "@/lib/push";
import { MessageList, Composer, WA } from "@/components/chat/ChatParts";

// "Chat with us": a private thread with the admin team. `buttonless` = panel only (opened via events).
export const ChatWidget = ({ buttonless = false }) => {
  const { user } = useAuth();
  return user && user.role !== "admin" ? <ChatWidgetInner buttonless={buttonless} /> : null; // admins reply from Admin → Chat
};

const ChatWidgetInner = ({ buttonless }) => {
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [unread, setUnread] = useState(0);

  const load = useCallback(async () => { const r = await api.chatMessages(); setMsgs(r.messages); setUnread(0); }, []);
  useEffect(() => {
    const onCount = (e) => !open && setUnread(e.detail || 0);
    const onOpen = () => setOpen(true);
    window.addEventListener("cm-chat-unread", onCount);
    window.addEventListener("cm-open-chat", onOpen);
    return () => { window.removeEventListener("cm-chat-unread", onCount); window.removeEventListener("cm-open-chat", onOpen); };
  }, [open]);
  useEffect(() => {
    if (!open) return undefined;
    load().catch(() => toast.error("Couldn't load chat"));
    const iv = setInterval(() => load().catch(() => {}), 5000);
    return () => clearInterval(iv);
  }, [open, load]);

  const send = async (text, file) => {
    try { const m = await api.chatSend(text, file); setMsgs((l) => [...l, m]); }
    catch (e) { toast.error(e?.response?.data?.detail || "Couldn't send"); throw e; }
  };

  return (
    <>
      {!buttonless && (
        <button onClick={() => setOpen((o) => !o)} data-testid="chat-with-us-btn"
          className="flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1.5 text-xs font-bold text-white transition-transform hover:scale-105 sm:px-3" style={{ background: WA.green }}>
          <MessageCircle size={14} /> <span className="hidden sm:inline">Chat with us</span>
          {unread > 0 && <span className="ml-0.5 rounded-full bg-white px-1.5 text-[10px] font-black text-[#00a884]" data-testid="chat-unread-badge">{unread}</span>}
        </button>
      )}
      {open && (
        <div className="fixed bottom-4 right-4 z-[9990] flex h-[560px] max-h-[80vh] w-[380px] max-w-[calc(100vw-2rem)] flex-col overflow-hidden rounded-2xl border border-white/10 shadow-2xl" data-testid="chat-widget-panel">
          <div className="flex items-center gap-3 px-4 py-3" style={{ background: WA.head }}>
            <div className="flex h-9 w-9 items-center justify-center rounded-full" style={{ background: WA.green }}><MessageCircle size={18} className="text-white" /></div>
            <div className="flex-1"><div className="text-sm font-bold text-[#e9edef]">Caption Motion Support</div><div className="text-[11px]" style={{ color: WA.muted }}>We usually reply within a few hours</div></div>
            <button onClick={() => setOpen(false)} data-testid="chat-widget-close" className="text-[#8696a0] hover:text-white"><X size={18} /></button>
          </div>
          <PhoneAlertsBar />
          {msgs.length === 0 && <div className="px-6 pt-6 text-center text-xs" style={{ background: WA.bg, color: WA.muted }}>Say hi! Ask anything about your project — you can attach screenshots too.</div>}
          <MessageList messages={msgs} mine={(m) => !m.from_admin} testId="chat-widget-messages" />
          <Composer onSend={send} testId="chat-widget" />
        </div>
      )}
    </>
  );
};

const PhoneAlertsBar = () => {
  const [on, setOn] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { pushEnabled().then(setOn).catch(() => setOn(false)); }, []);
  if (on === null || (!pushSupported() && !isIOS())) return null;
  const enable = async () => {
    setBusy(true);
    try { await enablePush(); setOn(true); toast.success("Phone alerts on — we'll notify you when support replies"); }
    catch (e) { toast.error(e.message || "Couldn't turn on alerts"); }
    setBusy(false);
  };
  return (
    <div className="flex items-center gap-2 border-b border-white/5 px-4 py-2 text-[11px]" style={{ background: "#182229", color: WA.muted }} data-testid="chat-alerts-bar">
      {on ? <><Check size={13} className="text-[#00a884]" /> Phone alerts are on for replies</> : (<>
        <BellRing size={13} className="text-[#00a884]" /> Get notified on this phone when we reply
        <button onClick={enable} disabled={busy} data-testid="chat-enable-alerts" className="ml-auto rounded-full px-2.5 py-1 font-bold text-white disabled:opacity-50" style={{ background: WA.green }}>{busy ? "…" : "Turn on"}</button>
      </>)}
    </div>
  );
};
